import React, { useState, useRef, useEffect } from 'react';
import './Episodes.css';

const Episodes = () => {
  // Liste des YouTube Shorts
  const youtubeShorts = [
    'Xe4LUJVqblc',
    'x6mupuhmZNc',
    'diI8_kAdl3g',
    'Rqa8ZzY-JBc',
    '8rgCB0SUliE',
    'MpG0Bn8i6qY',
    'lL_Eong-s_M'
  ].filter(id => id !== '');

  const [centerIndex, setCenterIndex] = useState(0);
  const [playingVideo, setPlayingVideo] = useState(null);
  const carouselRef = useRef(null);
  const itemRefs = useRef([]);

  const getYouTubeThumbnail = (videoId) => {
    if (!videoId) return null;
    return `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const handleVideoClick = (videoId) => {
    // Toggle: if same video is clicked, stop playing; otherwise play new video
    setPlayingVideo(playingVideo === videoId ? null : videoId);
  };

  const scrollLeft = () => {
    if (carouselRef.current && centerIndex > 0) {
      const targetIndex = centerIndex - 1;
      const targetItem = itemRefs.current[targetIndex];
      if (targetItem) {
        const containerRect = carouselRef.current.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();
        const scrollLeft = targetItem.offsetLeft - (containerRect.width / 2) + (itemRect.width / 2);
        carouselRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  };

  const scrollRight = () => {
    if (carouselRef.current && centerIndex < youtubeShorts.length - 1) {
      const targetIndex = centerIndex + 1;
      const targetItem = itemRefs.current[targetIndex];
      if (targetItem) {
        const containerRect = carouselRef.current.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();
        const scrollLeft = targetItem.offsetLeft - (containerRect.width / 2) + (itemRect.width / 2);
        carouselRef.current.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  };

  // Track center video for opacity effect
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const updateCenterIndex = () => {
      const containerRect = carousel.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      
      let closestIndex = 0;
      let closestDistance = Infinity;

      itemRefs.current.forEach((item, index) => {
        if (!item) return;
        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(itemCenter - containerCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      setCenterIndex(closestIndex);
    };

    // Initial update
    updateCenterIndex();

    // Update on scroll
    carousel.addEventListener('scroll', updateCenterIndex);
    
    // Update on resize
    window.addEventListener('resize', updateCenterIndex);

    return () => {
      carousel.removeEventListener('scroll', updateCenterIndex);
      window.removeEventListener('resize', updateCenterIndex);
    };
  }, [youtubeShorts.length]);

  if (youtubeShorts.length === 0) {
    return (
      <section className="episodes" id="episodes">
        <div className="container">
          <h2 className="episodes-title">YouTube Shorts</h2>
          <div className="section-divider"></div>
          <p className="episodes-subtitle">Découvrez nos derniers Shorts</p>
          <div className="shorts-placeholder">
            <p>Ajoutez les IDs des YouTube Shorts dans le code</p>
            <a 
              href="https://www.youtube.com/@letempsdunemousse/shorts" 
              target="_blank" 
              rel="noopener noreferrer"
              className="episodes-button"
            >
              Chaîne YouTube
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="episodes" id="episodes">
      <div className="episodes-background">
      </div>
      <div className="container">
        <h2 className="episodes-title">YouTube Shorts</h2>
        <div className="section-divider"></div>
        <p className="episodes-subtitle">Découvrez nos derniers Shorts</p>
        
        <div className="carousel-wrapper">
          <button className="carousel-nav carousel-nav-left" onClick={scrollLeft} aria-label="Précédent">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          <div className="carousel-container" ref={carouselRef}>
            <div className="carousel-track">
              {youtubeShorts.map((videoId, index) => {
                const distanceFromCenter = Math.abs(index - centerIndex);
                
                // Center: full size and opacity
                // Adjacent videos: smaller and transparent
                // Other videos: normal size but less prominent
                const isCenter = distanceFromCenter === 0;
                const isAdjacent = distanceFromCenter === 1;
                
                let itemClass = 'carousel-item';
                let opacity = 1;
                let scale = 0.85;
                let zIndex = 1;
                
                if (isCenter) {
                  itemClass += ' carousel-item-center';
                  opacity = 1;
                  scale = 1;
                  zIndex = 3;
                } else if (isAdjacent) {
                  if (index < centerIndex) {
                    itemClass += ' carousel-item-left';
                  } else {
                    itemClass += ' carousel-item-right';
                  }
                  opacity = 0.4;
                  scale = 0.75;
                  zIndex = 2;
                } else {
                  // Other videos: completely hidden
                  opacity = 0;
                  scale = 0.85;
                  zIndex = 0;
                }

                const isPlaying = playingVideo === videoId;

                return (
                  <div 
                    key={videoId} 
                    className={itemClass}
                    ref={(el) => (itemRefs.current[index] = el)}
                    style={{
                      opacity: opacity,
                      transform: `scale(${scale})`,
                      zIndex: zIndex,
                    }}
                    onClick={() => handleVideoClick(videoId)}
                  >
                    <div className="carousel-item-image">
                      {isPlaying ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                          title={`YouTube Short ${videoId}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="carousel-item-video"
                        />
                      ) : (
                        <img 
                          src={getYouTubeThumbnail(videoId)} 
                          alt={`YouTube Short ${videoId}`}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" dy="10.5" font-weight="bold" x="50%25" y="50%25" text-anchor="middle"%3EVidéo%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          <button className="carousel-nav carousel-nav-right" onClick={scrollRight} aria-label="Suivant">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="episodes-cta">
          <a 
            href="https://www.youtube.com/@letempsdunemousse/shorts" 
            target="_blank" 
            rel="noopener noreferrer"
            className="episodes-button"
          >
            Chaîne YouTube
          </a>
        </div>
      </div>
    </section>
  );
};

export default Episodes;
